import Container from '@mui/material/Container';
import AuthBrandHeader from '../../shared/AuthBrandHeader';
import { useColors } from '../../theme/ColorModeContext';

/**
 * Controller identity.
 *
 * Web3Pwn is a non-commercial project run by an individual, not a company, so
 * there is no legal entity, tax ID or registered address to publish: LSSI-CE
 * art. 10 attaches to providers for whom the service is an economic activity.
 * GDPR art. 13(1)(a) still applies: a controller must be identifiable and
 * contactable, which a project name plus a monitored address satisfies.
 *
 * If the project ever takes ads, sponsorship, payment, or becomes a funnel into
 * something commercial, that changes: LSSI would then require the operator's
 * name, NIF and address here and CAN-SPAM a postal address in email footers
 * (supplied by the API's MAIL_SENDER_POSTAL_ADDRESS setting).
 */
const CONTROLLER = {
    tradingName: 'WEB3PWN',
    email: 'admin@web3pwn.com',
};

const PrivacyPolicy = () => {
    const colors = useColors();

    return (
        <Container sx={{
            width: { xs: 'calc(100% - 28px)', md: 'min(90%, 960px)' },
            padding: 0,
            marginTop: '100px',
            marginBottom: '60px',
            position: 'relative',
            zIndex: 1,
            color: colors.text.primary,
            '& a': {
                color: colors.accent.cyan,
            },
            '& th, & td': {
                textAlign: 'left',
                verticalAlign: 'top',
                padding: '8px 14px 8px 0',
                borderBottom: `1px solid ${colors.border.subtle}`,
            },
          }} disableGutters maxWidth={false} className="page-enter">
            <AuthBrandHeader />

<h1>Privacy Policy</h1>
<p>Last updated: September 14, 2026</p>

<p>This Privacy Policy explains what personal data {CONTROLLER.tradingName} collects when You use the Service, why We collect it, what We are legally allowed to do with it, and the rights You have over it.</p>

<h2>1. Who We Are</h2>
<p>{CONTROLLER.tradingName} is a <strong>non-commercial project run by an individual based in Spain</strong>. It is free to use. There is no company behind it, there are no ads, nothing is sold, and no part of it is a paid service.</p>
<p>That individual is the data controller for the purposes of the GDPR. Contact: <a href={`mailto:${CONTROLLER.email}`}>{CONTROLLER.email}</a>. This address is monitored and is the right place for any question about your data or this Policy.</p>
<p>This Policy is written to meet the EU General Data Protection Regulation (GDPR) and Spanish Organic Law 3/2018 (LOPDGDD). No Data Protection Officer has been appointed, as none is required at this scale.</p>
<p>Being non-commercial does not reduce your rights under the GDPR, and this Policy does not ask you to give any of them up.</p>

<h2>2. Definitions</h2>
<ul>
<li><strong>Service</strong>: the Web3Pwn website and web application, including the Academy, Missions, challenge execution environment, leaderboard and profiles.</li>
<li><strong>Account</strong>: the registered user account You create to access the Service.</li>
<li><strong>Personal Data</strong>: any information relating to an identified or identifiable individual.</li>
<li><strong>Usage Data</strong>: data generated automatically by use of the Service or its infrastructure.</li>
<li><strong>Processing</strong>: anything done with Personal Data, including collecting, storing, using, sharing and deleting it.</li>
</ul>

<h2>3. What We Collect</h2>
<h3>Data You give Us</h3>
<ul>
<li><strong>Account data</strong>: username, email address, and a salted hash of your password. We never store your password itself.</li>
<li><strong>Consent records</strong>: whether you opted in to product update emails, when, from which part of the Service, the IP address the choice was made from, and the exact wording you were shown. We are required to be able to demonstrate consent, which is why the record is this specific.</li>
<li><strong>Content you submit</strong>: exploit code and solutions you write in the in-browser editor, which are executed in an isolated container to grade the challenge.</li>
</ul>
<h3>Data generated by your use</h3>
<ul>
<li><strong>Progress data</strong>: challenges solved, points, theory stage progress, quiz attempts and timestamps.</li>
<li><strong>Activity records</strong>: the days on which you were signed in and how many times you logged in; which challenges and theory stages you opened; how many compile and test runs and quiz answers you made, and how many failed. These are kept as per-day and per-challenge counts, not as a log of every action.</li>
<li><strong>Signup source</strong>: when you register, the website you arrived from (its domain only, never the full address), any campaign tags in the link you followed (utm_source, utm_medium, utm_campaign), and the page you first landed on. Your browser remembers this for up to 30 days until you register.</li>
<li><strong>Usage Data</strong>: IP address, browser type and version, pages visited, time and date of access, time spent on pages, and similar diagnostic data.</li>
</ul>
<h3>Publicly visible data</h3>
<p>Your username, points, rank and solved-challenge count appear on the public leaderboard and on your public profile page. <strong>Your email address is never shown publicly.</strong> If you do not want a public presence, do not register.</p>

<h2>4. Why We Process It, and On What Legal Basis</h2>
<table>
<thead>
<tr><th>Purpose</th><th>Data used</th><th>Legal basis</th></tr>
</thead>
<tbody>
<tr>
  <td>Creating and running your Account; letting you solve challenges and tracking your progress</td>
  <td>Account data, progress data, submitted content</td>
  <td>Performance of a contract, GDPR art. 6(1)(b)</td>
</tr>
<tr>
  <td>Confirming your email address, resetting your password, and sending notices about security or material changes to these terms</td>
  <td>Account data</td>
  <td>Performance of a contract, GDPR art. 6(1)(b)</td>
</tr>
<tr>
  <td>Sending product update emails about new challenges, tracks and platform news</td>
  <td>Email address, consent record</td>
  <td><strong>Your consent</strong>, GDPR art. 6(1)(a)</td>
</tr>
<tr>
  <td>Keeping the Service secure: rate limiting, abuse prevention, sandboxing challenge execution</td>
  <td>Usage Data, IP address</td>
  <td>Our legitimate interest in a Service that is not abused, GDPR art. 6(1)(f)</td>
</tr>
<tr>
  <td>Understanding how the Service is used so We can improve it</td>
  <td>Usage Data, progress data, activity records and signup source. We look at these per Account as well as in aggregate, to find where people get stuck or stop using the Service. Only the administrator sees them; they are never used for advertising, sold, or used to make decisions about you.</td>
  <td>Our legitimate interest in improving the Service, GDPR art. 6(1)(f)</td>
</tr>
<tr>
  <td>Complying with legal obligations and defending legal claims</td>
  <td>Whatever is relevant to the obligation or claim</td>
  <td>Legal obligation, GDPR art. 6(1)(c); legitimate interest, art. 6(1)(f)</td>
</tr>
</tbody>
</table>

<h2>5. Email We Send You</h2>
<p>We send two kinds of email and treat them differently.</p>

<h3>Service email: sent because you have an Account</h3>
<p>Address confirmation, password resets, security notices, and notices of material changes to this Policy or the Terms. These are part of providing the Service. You cannot unsubscribe from them while your Account exists, because they are how We reach you about your own Account. If you do not want them, delete your Account.</p>

<h3>Product update email: sent only if you opt in</h3>
<p>Announcements of new challenges, new chain tracks and platform news. We send these <strong>only</strong> if you have given specific consent, and We expect to send them at most a couple of times a month.</p>
<ul>
<li>Consent is given by ticking a box that is never pre-ticked and is separate from accepting the Terms. <strong>Registering does not subscribe you.</strong></li>
<li>Consent is never a condition of using the Service, and refusing costs you nothing.</li>
<li>We only send to addresses that have been confirmed.</li>
<li>Every such email carries a working one-click unsubscribe link, and You can also switch them off at any time under Settings.</li>
<li>Withdrawing consent is as easy as giving it, takes effect immediately, and does not affect the lawfulness of anything We sent beforehand. It does not stop service email.</li>
<li>We do not use tracking pixels to record whether you opened a message, and We do not sell, rent or share your email address for anyone else's marketing. There is nothing to advertise to you: the project is free and non-commercial.</li>
</ul>
<p>If you unsubscribe, We keep a record that you did so; that record is how We make sure you are not contacted again, and how We can demonstrate the opt-out was honoured.</p>

<h2>6. Cookies and Similar Technologies</h2>
<p>We use browser storage and cookies for the following purposes only:</p>
<ul>
<li><strong>Strictly necessary</strong>: holding your session token so you stay logged in. Without these the Service cannot work.</li>
<li><strong>Functionality</strong>: remembering preferences such as light or dark colour mode and dismissed prompts.</li>
<li><strong>Analytics</strong>: Google Analytics 4, used to understand aggregate usage patterns; and a first-party browser storage entry that remembers where you first arrived from until you register (see section 3).</li>
</ul>
<p>Your browser can be set to refuse cookies, but the Service will not work correctly without the strictly necessary ones.</p>

<h2>7. Who We Share It With</h2>
<p>We do not sell your Personal Data. We share it only with:</p>
<ul>
<li><strong>Service providers acting on our instructions</strong>: hosting, email delivery and analytics. Each is bound by a data processing agreement under GDPR art. 28 and may only use the data to provide their service to Us.</li>
<li><strong>Public areas of the Service</strong>: your username and progress, as described in section 3.</li>
<li><strong>Authorities</strong>: where We are legally required to disclose, or where it is necessary to establish, exercise or defend legal claims.</li>
<li><strong>A successor</strong>: if the Service is merged with or acquired by another organisation, in which case We will notify you before your data becomes subject to a different policy.</li>
</ul>

<h2>8. International Transfers</h2>
<p>Some of our service providers process data outside the European Economic Area. Where that happens, the transfer is covered by an adequacy decision of the European Commission or by Standard Contractual Clauses together with any additional safeguards needed. You may request a copy of the relevant safeguards from the contact address in section 1.</p>

<h2>9. How Long We Keep It</h2>
<table>
<thead>
<tr><th>Data</th><th>Retained for</th></tr>
</thead>
<tbody>
<tr><td>Account data and progress</td><td>As long as your Account exists</td></tr>
<tr><td>Submitted exploit code</td><td>Written to a temporary file, executed in an isolated container, and discarded when the run finishes</td></tr>
<tr><td>Consent and unsubscribe records</td><td>While your Account exists; deleted with the Account</td></tr>
<tr><td>Suppression list entries (addresses that bounced or complained)</td><td>Kept so We do not contact the address again</td></tr>
<tr><td>Activity records and signup source</td><td>As long as your Account exists; deleted with the Account</td></tr>
<tr><td>Other Usage Data and analytics</td><td>Retained in aggregate; individual-level records are kept only as long as needed for security and diagnostics</td></tr>
</tbody>
</table>
<p>Deleting your Account removes your Account data, your solved-challenge records, your activity records and signup source, and your consent history. We may retain what We are legally obliged to retain, or what is necessary to defend a legal claim.</p>

<h2>10. Your Rights</h2>
<p>Under the GDPR you have the right to:</p>
<ul>
<li><strong>Access</strong> the Personal Data We hold about you and obtain a copy.</li>
<li><strong>Rectify</strong> data that is inaccurate or incomplete.</li>
<li><strong>Erase</strong> your data: you can do this yourself at any time under Settings, which deletes your Account.</li>
<li><strong>Restrict</strong> processing in certain circumstances.</li>
<li><strong>Data portability</strong>: receive the data you gave Us in a structured, commonly used, machine-readable format.</li>
<li><strong>Object</strong> to processing based on our legitimate interests. <strong>Objection to direct marketing is absolute:</strong> if you object, We stop, with no further questions.</li>
<li><strong>Withdraw consent</strong> at any time, without affecting the lawfulness of processing carried out beforehand.</li>
</ul>
<p>To exercise any of these, contact <a href={`mailto:${CONTROLLER.email}`}>{CONTROLLER.email}</a>. We will respond within one month. There is no charge unless a request is manifestly unfounded or excessive.</p>
<p>If you believe We have handled your data unlawfully, you may lodge a complaint with the Spanish supervisory authority, the <strong>Agencia Española de Protección de Datos</strong> (<a href="https://www.aepd.es" target="_blank" rel="noreferrer">www.aepd.es</a>), or with the authority in your country of residence.</p>

<h2>11. Security</h2>
<p>Passwords are stored salted and hashed, never in plain text. Access to the Service is authenticated with expiring bearer tokens. Challenge code you submit runs in a container with no network access and enforced CPU, memory and process limits. No method of transmission or storage is perfectly secure, and We cannot guarantee absolute security, but We take reasonable technical and organisational measures appropriate to the risk.</p>

<h2>12. Children</h2>
<p>The Service is not directed at anyone under 14, the age at which a child can consent to information society services under Spanish law. We do not knowingly collect data from children under that age. If you believe a child has given Us Personal Data, contact Us and We will delete it.</p>

<h2>13. Links to Other Sites</h2>
<p>The Service links to third-party sites We do not control, including documentation and community platforms. We are not responsible for their content or their privacy practices. Review their policies before providing them with data.</p>

<h2>14. Changes to This Policy</h2>
<p>We may update this Policy. When We do, We update the "Last updated" date above. If a change materially affects your rights (for example, a new purpose for your data) We will notify you by email before it takes effect. Continuing to use the Service after a change means you accept the updated Policy.</p>

<h2>15. Contact</h2>
<p>Questions about this Policy or about your data: <a href={`mailto:${CONTROLLER.email}`}>{CONTROLLER.email}</a></p>
        </Container>
    )
};

export default PrivacyPolicy;
